import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { BUSINESS } from '@shared/config'
import * as styles from './Header.css'

function classNames(...values: Array<string | false | undefined>) {
  return values.filter(Boolean).join(' ')
}

export function Header() {
  const navigate = useNavigate()
  const location = useLocation()
  const { locale = 'kor' } = useParams<{ locale: string }>()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isSolid, setIsSolid] = useState(false)

  const homePath = `/${locale}`
  const isHome = location.pathname === homePath || location.pathname === `${homePath}/`

  useEffect(() => {
    const onScroll = () => setIsSolid(window.scrollY > 40)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [isMenuOpen])

  // 홈 이외 페이지에서는 항상 밝은 배경 위에 놓이므로 solid(검은 텍스트)로 고정
  const solid = isSolid || isMenuOpen || !isHome

  // 홈에 실제로 렌더링되는 섹션(#solutions·#rental·#reference·#inquiry)만 연결.
  // #reason·#business 섹션은 현재 홈에서 주석 처리되어 있어 제외.
  const sectionLinks = [
    { label: '메뉴', href: '#solutions' },
    { label: '가맹 안내', href: '#rental' },
    { label: '라멘 이야기', href: '#reference' },
    { label: '창업 상담', href: '#inquiry' },
  ]

  const desktopLinks = [
    ...sectionLinks,
    { label: '라멘 블로그', href: `/${locale}/blog` },
  ]

  const scrollToSection = (href: string) => {
    if (href === '#top') {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }
    document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' })
  }

  const handleNav = (href: string) => {
    setIsMenuOpen(false)
    // 라우트 이동 (블로그 등)
    if (href.startsWith('/')) {
      window.setTimeout(() => navigate(href), isMenuOpen ? 220 : 0)
      return
    }
    // 섹션 앵커 — 홈이 아니면 홈으로 이동 후 스크롤
    if (!isHome) {
      navigate(homePath)
      window.setTimeout(() => scrollToSection(href), 400)
      return
    }
    window.setTimeout(() => scrollToSection(href), isMenuOpen ? 220 : 0)
  }

  return (
    <>
      <header className={classNames(styles.header, solid ? styles.headerSolid : styles.headerOverlay)}>
        <a href={homePath} className={styles.brand} aria-label="우마이식당 홈" onClick={(event) => { event.preventDefault(); handleNav('#top') }}>
          <img className={styles.brandLogo} src="/images/yumaiLogo.webp" alt="우마이식당" />
        </a>
        <nav className={styles.desktopNav} aria-label="주요 메뉴">
          {desktopLinks.map((link) => <a key={link.label} href={link.href} className={styles.desktopNavLink} onClick={(event) => { event.preventDefault(); handleNav(link.href) }}>{link.label}</a>)}
        </nav>
        <div className={styles.headerActions}>
          <a className={styles.headerUtilityAccent} href={`/${locale}/blog`} onClick={(event) => { event.preventDefault(); handleNav(`/${locale}/blog`) }}>Blog</a>
          <button
            type="button"
            className={styles.menuButton}
            style={{ color: solid ? styles.ink : '#fff' }}
            aria-label={isMenuOpen ? '메뉴 닫기' : '메뉴 열기'}
            aria-expanded={isMenuOpen}
            onClick={() => setIsMenuOpen((open) => !open)}
          >
            <span className={styles.menuLines} aria-hidden="true">
              <span className={classNames(styles.menuLine, styles.menuLineTop, isMenuOpen && styles.menuLineTopOpen)} />
              <span className={classNames(styles.menuLine, styles.menuLineMid, isMenuOpen && styles.menuLineMidOpen)} />
              <span className={classNames(styles.menuLine, styles.menuLineBottom, isMenuOpen && styles.menuLineBottomOpen)} />
            </span>
          </button>
        </div>
      </header>
      {isMenuOpen && (
        <nav className={styles.menuOverlay} aria-label="주요 메뉴">
          <div className={styles.menuList}>
            {sectionLinks.map((link) => (
              <div key={link.label} className={styles.menuGroup}>
                <div className={styles.menuRow}>
                  <a href={link.href} className={styles.menuLink} onClick={(event) => { event.preventDefault(); handleNav(link.href) }}>
                    <span>{link.label}</span>
                  </a>
                </div>
              </div>
            ))}
            <a className={styles.menuBlogLink} href={`/${locale}/blog`} onClick={(event) => { event.preventDefault(); handleNav(`/${locale}/blog`) }}>Blog</a>
          </div>
        </nav>
      )}
    </>
  )
}
